import { ReactNode } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../services/auth'
import { UserRole } from '../types'
import { useSidebar } from '../contexts/SidebarContext'
import { getGroupedSettingsTabs, DEFAULT_SETTINGS_TAB } from '../config/settingsTabs'

interface SidebarProps {
  isOpen?: boolean
  onClose?: () => void
}

export default function Sidebar({ isOpen = false, onClose }: SidebarProps) {
  const { user } = useAuthStore()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { sidebarCollapsed, setSidebarCollapsed } = useSidebar()

  /**
   * The settings sections expand in place under the Settings entry instead of
   * forming a second menu column on the page. Collapsed, the rail is only wide
   * enough for icons, so the sub-menu stays hidden and Settings behaves as a
   * plain link.
   */
  const isSettingsOpen = location.pathname === '/settings' && !sidebarCollapsed
  const settingsGroups = getGroupedSettingsTabs(user?.role)
  const activeSettingsTab = searchParams.get('tab') || DEFAULT_SETTINGS_TAB

  const isActive = (path: string) => {
    return location.pathname === path
  }

  type NavItem = {
    name: string
    path: string
    icon: ReactNode
    /** Nested entries, revealed while the section they belong to is open. */
    subItems?: { name: string; path: string; icon: ReactNode }[]
  }

  const menuItems = [
    // Dashboard button temporarily hidden
    // {
    //   name: 'Dashboard',
    //   path: '/',
    //   icon: (
    //     <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    //     </svg>
    //   ),
    //   public: true
    // },
    {
      name: 'Auctions',
      path: '/auctions',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
      public: true
    }
  ]

  const bidderMenuItems = [
    {
      name: 'My Activity',
      path: '/my-activity',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    }
  ]

  const authenticatedMenuItems = [
    {
      name: 'Settings',
      path: '/settings',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    }
  ]

  const auctioneerMenuItems: NavItem[] = [
    {
      name: 'Marketing',
      path: '/marketing',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
        </svg>
      ),
      subItems: [
        {
          name: 'Create',
          path: '/marketing/create',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
            </svg>
          ),
        },
        {
          name: 'Library',
          path: '/marketing/library',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          ),
        },
      ],
    },
    {
      name: 'Users',
      path: '/users',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
        </svg>
      )
    },
    {
      name: 'Transactions',
      path: '/transactions',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    }
  ]

  const staffMenuItems = [
    {
      name: 'Users',
      path: '/users',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
        </svg>
      )
    }
  ]

  const systemAdminMenuItems = [
    {
      name: 'Users',
      path: '/users',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
        </svg>
      )
    },
    {
      name: 'Transactions',
      path: '/transactions',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    },
    {
      name: 'Notifications',
      path: '/notifications',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4.343 12.344l1.414 1.414L9 10.414V9a3 3 0 616 0v1.414l3.243 3.243 1.414-1.414L12 4.343z" />
        </svg>
      )
    }
  ]



  const handleLinkClick = () => {
    if (onClose) {
      onClose()
    }
  }

  // Switching section keeps the page's scroll offset otherwise, dropping the
  // reader partway down the new form.
  const handleSettingsTabClick = () => {
    handleLinkClick()
    window.scrollTo({ top: 0 })
  }

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar. Fixed, so it sits outside the padded page wrapper and has to
          clear the impersonation banner itself - the custom property is 0px
          when no banner is shown. */}
      <aside
        className={`
        fixed left-0 ${sidebarCollapsed ? 'w-16' : 'w-64'} bg-white dark:bg-dark-secondary shadow-lg border-r border-gray-200 dark:border-dark-primary z-50 transform transition-all duration-300 ease-in-out flex flex-col overflow-x-hidden
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0 md:block
      `}
        style={{
          top: 'var(--impersonation-banner-height)',
          height: 'calc(100vh - var(--impersonation-banner-height))',
        }}
      >
        {/* Header with Logo and Collapse Button. Height is pinned to the app
            header's h-16 rather than left to the padding, so the two bottom
            borders meet in a single line across the top of the page - the
            collapse button's own padding makes this row 36px tall otherwise,
            dropping the divider 4px below the header's. */}
        <div className="h-16 px-4 flex items-center border-b border-gray-200 dark:border-dark-primary">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center">
              {/* Logo - Clickable when collapsed */}
              <div className="flex-shrink-0">
                <div 
                  className={`w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center ${sidebarCollapsed ? 'cursor-pointer hover:bg-gradient-to-br hover:from-blue-600 hover:to-purple-700 transition-all' : ''}`}
                  onClick={sidebarCollapsed ? () => setSidebarCollapsed(false) : undefined}
                  title={sidebarCollapsed ? 'Expand sidebar' : undefined}
                >
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
              </div>
              
              {/* Title and Role */}
              {!sidebarCollapsed && (
                <div className="ml-3">
                  <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Charity Auctions</h1>
                </div>
              )}
            </div>
            
            {/* Collapse Button - Only shown when expanded */}
            {!sidebarCollapsed && (
              <button
                onClick={() => setSidebarCollapsed(true)}
                className="hidden md:block p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-colors"
                title="Collapse sidebar"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7M21 19l-7-7 7-7" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Mobile close button */}
        <div className="md:hidden flex justify-end p-4 border-b border-gray-200 dark:border-dark-primary">
          <button
            onClick={onClose}
            className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-colors"
            aria-label="Close menu"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <nav className={`${sidebarCollapsed ? 'p-1' : 'p-4'} space-y-2 overflow-y-auto overflow-x-hidden flex-1`}>
          {/* Public menu items */}
          {menuItems.map((item) => (
            <div key={item.path} className="relative group">
              <Link
                to={item.path}
                onClick={handleLinkClick}
                className={`flex items-center ${sidebarCollapsed ? 'justify-center w-12 h-12' : 'space-x-3 px-3 py-3'} rounded-lg text-sm font-medium transition-colors touch-manipulation ${
                  isActive(item.path)
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-tertiary hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-dark-tertiary">{item.icon}</span>
                {!sidebarCollapsed && <span>{item.name}</span>}
              </Link>
              
              {/* Tooltip for collapsed state */}
              {sidebarCollapsed && (
                <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                  {item.name}
                </div>
              )}
            </div>
          ))}

          {/* Bidder menu items */}
          {user && user.role === UserRole.BIDDER && (
            <>
              <div className="border-t border-gray-200 dark:border-dark-primary my-4"></div>
              {bidderMenuItems.map((item) => (
                <div key={item.path} className="relative group">
                  <Link
                    to={item.path}
                    onClick={handleLinkClick}
                    className={`flex items-center ${sidebarCollapsed ? 'justify-center w-12 h-12' : 'space-x-3 px-3 py-3'} rounded-lg text-sm font-medium transition-colors touch-manipulation ${
                      isActive(item.path)
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-tertiary hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-dark-tertiary">{item.icon}</span>
                    {!sidebarCollapsed && <span>{item.name}</span>}
                  </Link>
                  
                  {/* Tooltip for collapsed state */}
                  {sidebarCollapsed && (
                    <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                      {item.name}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {/* Auctioneer menu items */}
          {user && user.role === UserRole.AUCTIONEER && (
            <>
              <div className="border-t border-gray-200 dark:border-dark-primary my-4"></div>
              {auctioneerMenuItems.map((item) => (
                <div key={item.path} className="relative group">
                  <Link
                    to={item.path}
                    onClick={handleLinkClick}
                    className={`flex items-center ${sidebarCollapsed ? 'justify-center w-12 h-12' : 'space-x-3 px-3 py-3'} rounded-lg text-sm font-medium transition-colors touch-manipulation ${
                      isActive(item.path) || (item.subItems && location.pathname.startsWith(`${item.path}/`))
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-tertiary hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-dark-tertiary">{item.icon}</span>
                    {!sidebarCollapsed && <span>{item.name}</span>}
                  </Link>

                  {/* Nested entries. Always listed, not just once the section is
                      open - they are destinations in their own right, and gating
                      them on being there already makes them unreachable from
                      anywhere else. Hidden only on a collapsed rail, which is
                      just wide enough for icons. */}
                  {item.subItems && !sidebarCollapsed && (
                    <div className="mt-1 ml-5 pl-3 border-l border-gray-200 dark:border-dark-primary space-y-0.5">
                      {item.subItems.map(subItem => (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          onClick={handleLinkClick}
                          className={`flex items-center space-x-2 px-2 py-2 rounded-lg text-sm transition-colors touch-manipulation ${
                            isActive(subItem.path)
                              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-tertiary hover:text-gray-900 dark:hover:text-gray-100'
                          }`}
                        >
                          <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center [&>svg]:w-4 [&>svg]:h-4">
                            {subItem.icon}
                          </span>
                          <span className="truncate">{subItem.name}</span>
                        </Link>
                      ))}
                    </div>
                  )}

                  {/* Tooltip for collapsed state */}
                  {sidebarCollapsed && (
                    <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                      {item.name}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {/* Staff menu items */}
          {user && user.role === UserRole.STAFF && (
            <>
              <div className="border-t border-gray-200 dark:border-dark-primary my-4"></div>
              {staffMenuItems.map((item) => (
                <div key={item.path} className="relative group">
                  <Link
                    to={item.path}
                    onClick={handleLinkClick}
                    className={`flex items-center ${sidebarCollapsed ? 'justify-center w-12 h-12' : 'space-x-3 px-3 py-3'} rounded-lg text-sm font-medium transition-colors touch-manipulation ${
                      isActive(item.path)
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-tertiary hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-dark-tertiary">{item.icon}</span>
                    {!sidebarCollapsed && <span>{item.name}</span>}
                  </Link>
                  
                  {/* Tooltip for collapsed state */}
                  {sidebarCollapsed && (
                    <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                      {item.name}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {/* System Admin menu items */}
          {user && user.role === UserRole.SYSTEM_ADMIN && (
            <>
              <div className="border-t border-gray-200 dark:border-dark-primary my-4"></div>
              {systemAdminMenuItems.map((item) => (
                <div key={item.path} className="relative group">
                  <Link
                    to={item.path}
                    onClick={handleLinkClick}
                    className={`flex items-center ${sidebarCollapsed ? 'justify-center w-12 h-12' : 'space-x-3 px-3 py-3'} rounded-lg text-sm font-medium transition-colors touch-manipulation ${
                      isActive(item.path)
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-tertiary hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-dark-tertiary">{item.icon}</span>
                    {!sidebarCollapsed && <span>{item.name}</span>}
                  </Link>
                  
                  {/* Tooltip for collapsed state */}
                  {sidebarCollapsed && (
                    <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                      {item.name}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {/* Authenticated user menu items */}
          {user && (
            <>
              <div className="border-t border-gray-200 dark:border-dark-primary my-4"></div>
              {authenticatedMenuItems.map((item) => (
                <div key={item.path} className="relative group">
                  <Link
                    to={item.path}
                    onClick={handleLinkClick}
                    className={`flex items-center ${sidebarCollapsed ? 'justify-center w-12 h-12' : 'space-x-3 px-3 py-3'} rounded-lg text-sm font-medium transition-colors touch-manipulation ${
                      isActive(item.path)
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-tertiary hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-dark-tertiary">{item.icon}</span>
                    {!sidebarCollapsed && <span>{item.name}</span>}
                  </Link>

                  {/* Settings sections, nested under Settings while that page is
                      open. The rule on the left ties the group back to its
                      parent entry so the indent reads as hierarchy. */}
                  {item.path === '/settings' && isSettingsOpen && (
                    <div className="mt-1 ml-5 pl-3 border-l border-gray-200 dark:border-dark-primary space-y-4">
                      {settingsGroups.map(section => (
                        <div key={section.group}>
                          <h2 className="px-2 mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                            {section.group}
                          </h2>
                          <div className="space-y-0.5">
                            {section.items.map(tab => (
                              <Link
                                key={tab.id}
                                to={`/settings?tab=${tab.id}`}
                                onClick={handleSettingsTabClick}
                                className={`flex items-center space-x-2 px-2 py-2 rounded-lg text-sm transition-colors touch-manipulation ${
                                  activeSettingsTab === tab.id
                                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-tertiary hover:text-gray-900 dark:hover:text-gray-100'
                                }`}
                              >
                                <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center [&>svg]:w-4 [&>svg]:h-4">
                                  {tab.icon}
                                </span>
                                <span className="truncate">{tab.name}</span>
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Tooltip for collapsed state */}
                  {sidebarCollapsed && (
                    <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                      {item.name}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </nav>
      </aside>
    </>
  )
}
